// src/components/shared/TermsAndPrivacyPage.tsx
// עמוד תנאי שימוש ומדיניות פרטיות - נגיש לכולם

import React from "react";
import { useNavigate } from "react-router-dom";
import AccessibilityWidget from "./AccessibilityWidget";

const TermsAndPrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      {/* Accessibility Widget */}
      <AccessibilityWidget />

      <main
        className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
        dir="rtl"
      >
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-8 sm:p-12">
          {/* Header with Back Button */}
          <div className="mb-8 flex items-center justify-between border-b pb-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                📋 תנאי שימוש ומדיניות פרטיות
              </h1>
              <p className="text-lg text-gray-600">עודכן לאחרונה: 01/01/2026</p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              aria-label="חזור לעמוד הקודם"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span className="hidden sm:inline">חזור</span>
            </button>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* כללי */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                כללי
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>1.</strong> אתר{" "}
                  <a
                    href="https://www.cartax.org"
                    className="text-blue-600 hover:text-blue-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    www.cartax.org
                  </a>{" "}
                  (להלן: "האתר") מספק שירותי סימולציה/אומדן מיסי יבוא לכלי רכב
                  ביבוא אישי או מסחרי.
                </p>

                <p>
                  <strong>2.</strong> האתר מופעל בידי קאר טאקס בע״מ, ח.פ.
                  517152609 (להלן: "החברה"), מכתובת הסוללים, מיקוד 1790500, ת.ד.
                  139.
                </p>

                <p>
                  <strong>3.</strong> גלישה ושימוש באתר — לרבות הרשמה ורכישת
                  מנוי — מהווים הסכמה מחייבת לתנאי שימוש אלה ולמדיניות הפרטיות,
                  שהם חלק בלתי נפרד זה מזה. אם אינך מסכים לתנאים — הימנע משימוש
                  באתר.
                </p>

                <p>
                  <strong>4.</strong> הניסוח בלשון זכר מטעמי נוחות בלבד ומכוון
                  לכל המגדרים; כותרות הפרקים נועדו לנוחות בלבד.
                </p>

                <p>
                  <strong>5.</strong> הגישה והשימוש באתר אינטרנט זה לרבות השימוש
                  בכל התכנים ו/או המידע ו/או השירותים המופיעים בו מפעם לפעם כפוף
                  לתנאי השימוש המפורטים להלן ולהוראות כל דין החל במדינת ישראל.
                </p>

                <p>
                  <strong>6.</strong> השימוש באתר מוגבל אך ורק למטרות קבלת
                  השירותים. אין להעתיק, לשכפל, להפיץ, למכור ו/או להציג מוצר
                  כלשהו אלא אם ניתנה רשות לכך מראש ובכתב מהחברה, כמפורט בפרק
                  קניין רוחני להלן.
                </p>

                <p>
                  <strong>7.</strong> כל אדם או חברה המבצע/ת פעולה כלשהי באתר,
                  לרבות ביצוע רכישה (להלן: "הלקוח"), מצהיר/ה כי קרא/ה את תקנון
                  זה, הבין/ה את תנאיו, וכי הוא/היא מסכים/ה להם.
                </p>

                <p>
                  <strong>8.</strong> הלקוח מצהיר כי הינו כשיר לבצע פעולות
                  משפטיות מחייבות. במידה והלקוח הינו מתחת לגיל 18, חובה עליו
                  ליידע את האפוטרופוס החוקי בדבר הוראות האתר והתקנון ולקבל ממנו
                  אישור מראש לביצוע הפעולות באתר.
                </p>

                <p>
                  <strong>9.</strong> השירות שניתן באתר מוענק בתחומי מדינת ישראל
                  ובאזורים גאוגרפיים נוספים כפי שיוגדרו מעת לעת על-ידי החברה
                  (להלן: "אזור השירות").
                </p>

                <p>
                  <strong>10.</strong> החברה תהא רשאית, מעת לעת ולפי שיקול דעתה
                  הבלעדי, לשנות את ההיצע ו/או מגוון השירותים/המוצרים המוצגים
                  באתר, להחליף ו/או להוסיף ו/או להסיר מהם.
                </p>

                <p>
                  <strong>11.</strong> הלקוח מתחייב לשפות את החברה בגין כל נזק
                  שיגרם לה עקב מסירת פרטים כוזבים ושיבוש הפעלת האתר.
                </p>

                <p>
                  <strong>12.</strong> סרטוני הדרכה, תמונות והצגת המוצרים הינן
                  לשם המחשה בלבד ואינם מחייבים את החברה.
                </p>
              </div>
            </section>

            {/* שימוש באתר - תשלום, מנוי והרשמה */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                שימוש באתר - תשלום, מנוי והרשמה
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>13.</strong> הרישום מותנה ברכישת מנוי. סוגי המנויים
                  ומחיריהם עשויים להשתנות מעת לעת לפי שיקול דעת החברה. בתום תוקף
                  המנוי תיחסם הגישה למערכת, ותישאר גישה לאזור האישי לצפייה
                  בחישובים קודמים.
                </p>

                <p>
                  <strong>14.</strong> התשלום מתבצע בעמוד חיצוני מאובטח של GROW.
                  פרטי האשראי מעובדים ומאוחסנים ב-GROW ואינם נשמרים באתר CARTAX
                  בשום שלב.
                </p>

                <p>
                  <strong>15.</strong> בעת ההרשמה יימסרו: שם מלא (או שם תאגיד),
                  דוא״ל ומספר נייד; מספר עוסק – רשות. לאחר מילוי הפרטים יופנה
                  המשתמש לעמוד התשלום ב-GROW.
                </p>

                <p>
                  <strong>16.</strong> עם השלמת תשלום תקין, יישלח אישור בדוא״ל
                  והמנוי יופעל. באי־קבלת אישור – יש לפנות לשירות הלקוחות.
                </p>

                <p>
                  <strong>17.</strong> רישומי המערכת ופרטי ההזמנה יהוו ראיה
                  לכאורה לאמור בהם. מסירת פרטים כוזבים עלולה להקים עילה לפי דין.
                </p>

                <p>
                  <strong>18.</strong> המנויים אינם מתחדשים אוטומטית; החברה
                  רשאית לשלוח תזכורות לפני תום המנוי ולאחריו.
                </p>

                <p>
                  <strong>19.</strong> שירות מנוי בתשלום יקרא להלן "שירותי
                  החברה" לעניין מסמך זה, "שירותי החברה" – שירותי מנוי בתשלום
                  הכוללים גישה למערכת הסימולציה/אומדן.
                </p>

                <p>
                  <strong>20.</strong> הלקוח מצהיר בזאת כי הוא מודע לעובדה שכל
                  תפקידה של החברה בקשר עם שירותי החברה הוא לספק שירות
                  אומדן/סימולציית מס ביבוא כלי רכב לישראל בלבד.
                </p>

                <p className="bg-yellow-50 border-r-4 border-yellow-400 p-4 rounded">
                  <strong>21.</strong> מובהר כי החברה עושה מאמץ לספק
                  אומדן/סימולציית מס מדויקים ככל האפשר. יחד עם זאת, מדובר בהערכה
                  בלבד, בין השאר בשל גורמים שאינם בשליטת החברה, כגון: מיסים
                  ואגרות במדינת היצוא, בדיקות מכס בנמלי ההטעינה או הפריקה,
                  שינויי שערי חליפין ממועד ההזמנה ועד לשחרור, שינויי תעריפי
                  שינוע אצל חברות ספנות/תעופה, עלויות בלתי צפויות בנמלים (לרבות
                  אחסנה והשהייה), פסילת ערך עסקה והוצאת גירעון על-ידי רשות
                  המיסים, וכן קנסות או כופר על-ידי המכס או משרד התחבורה.
                </p>
              </div>
            </section>

            {/* אחריות האתר */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                אחריות האתר
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>22.</strong> האתר והשירותים ניתנים כמות שהם (As Is).
                  ייתכן שהמידע אינו שלם/מדויק/עדכני ועלולות להתרחש טעויות, תקלות
                  או אי־זמינות.
                </p>

                <p>
                  <strong>23.</strong> החברה לא תהיה אחראית לכל נזק ישיר או עקיף
                  (לרבות אובדן רווח או עוגמת נפש) עקב שימוש, הסתמכות או
                  אי־זמינות האתר, קישורים חיצוניים או תכני צדדים שלישיים.
                </p>

                <p>
                  <strong>24.</strong> כל שימוש או הסתמכות על המידע באתר הם
                  באחריות המשתמש בלבד. מומלץ לעיין בקפידה בתיאור השירות ולהצליב
                  נתונים לפי הצורך.
                </p>
              </div>
            </section>

            {/* מדיניות ביטולים */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                מדיניות ביטולים
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>25.</strong> האמור בסעיף זה כפוף לחוק הגנת הצרכן
                  התשמ״א-1981 (להלן: "החוק").
                </p>

                <p>
                  <strong>26.</strong> מנוי יומי הוא ל24 שעות בדיוק. מנוי חודשי
                  הוא ל־30 ימים בדיוק. לאחר הרכישה לא ניתן לבטל. מועד החיוב
                  וסיום המנוי ייקבעו לפי מועד ושעת הרכישה.
                </p>

                <p>
                  <strong>27.</strong> במידה ומדובר במנוי ל-3 חודשים, ניתן יהיה
                  לבטל את המנוי בכל שלב לאחר הרכישה ולקבל החזר כספי יחסי לפי
                  חיוב של מנוי חודשי רגיל. כלומר אם בחר משתמש פלוני לסיים את
                  המנוי שלו לאחר חודש הוא יקבל החזר כספי מלא אך לפי תמחור של
                  מנוי חודשי רגיל.
                </p>

                <p>
                  <strong>28.</strong> במידה ומדובר במנוי שנתי ל-12 חודשים ניתן
                  יהיה לבטל את המנוי בכל שלב לאחר הרכישה ולקבל החזר כספי יחסי
                  לפי חיוב של מנוי חודשי רגיל. כלומר אם בחר משתמש פלוני לסיים את
                  המנוי שלו לאחר חודש הוא יקבל החזר כספי מלא אך לפי תמחור של
                  מנוי חודשי רגיל.
                </p>

                <p>
                  <strong>29.</strong> במידה ומדובר בשירותים מתמשכים, קרי
                  שירותים אשר ניתנים מחודש לחודש והתשלום עבורם מתחדש בהתאם, ניתן
                  יהא לבטל את השירות בהתאם לחוק הגנת הצרכן, בהודעה כאמור לאמצעי
                  הקשר המצ"ב מטה. הביטול ייכנס בהתאם לקבוע בחוק הגנת הצרכן (תוך
                  3 ימי עסקים).
                </p>

                <p>
                  <strong>30.</strong> ככל ולקוח עומד בקריטריונים לעיל בנוגע
                  לביטול איזה מהשירותים המוצעים על ידי החברה ויהיה מעוניין לבטל
                  את ההזמנה, ביטול ההזמנה יהיה כרוך בעמלת דמי טיפול של 5% או 100
                  ₪, זיכוי כספי יינתן ככל שהדין מתיר.
                </p>

                <p className="bg-blue-50 border-r-4 border-blue-400 p-4 rounded">
                  <strong>31.</strong> הודעה על ביטול עסקה תתבצע באמצעות דוא"ל –{" "}
                  <a
                    href="mailto:info@cartax.org"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    info@cartax.org
                  </a>
                </p>
              </div>
            </section>

            {/* קניין רוחני */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                קניין רוחני
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>32.</strong> מלוא זכויות היוצרים, סימני המסחר והקניין
                  הרוחני באתר, לרבות בתכני האתר, בעיצוב האתר, ובכל תוכנה, יישום,
                  קוד מחשב, קובץ גרפי, תמונה, טקסט וכל חומר אחר הכלול באתר, הינם
                  בבעלות החברה בלבד. אין להעתיק, לצלם, להפיץ, להציג בפומבי או
                  למסור לצד שלישי אחד מאלו.
                </p>

                <p>
                  <strong>33.</strong> אין לשכפל, להעתיק, לפרסם, לשווק, להפיץ
                  ו/או לייצר עבודות נגזרות של כל חלק מן האתר.
                </p>

                <p>
                  <strong>34.</strong> דומיין האתר, האתר, שם המותג, סימני המסחר
                  (בין אם נרשמו ובין אם לאו), פיתוחי תוכנה שיפותחו על ידי החברה
                  וכיוצא בזה - הם כולם רכושה של החברה בלבד. אין לעשות בהם שימוש
                  בלא קבלת הסכמתה בכתב ומראש. כל מידע ו/או תצוגה המופיעים באתר,
                  לרבות גרפיקה, עיצוב, הצגה מילולית, סימני מסחר, סימני לוגו כן
                  עריכתם והצגתם של אלה, הנם בבעלות בלעדית של החברה ו/או מי
                  מטעמה. אין להעתיק לשכפל, להפיץ, לפרסם או להשתמש בכל דרך אחרת
                  את התכנים המופיעים באתר אלא אם החברה נתנה את הסכמתה לכך, בכתב
                  ומראש ואין בעצם הכניסה לאתר כדי להעניק רישיון ו/או זכות כלשהי
                  כל המוצרים המוצגים באתר הינם חלק מקניינה הרוחני של בעלת המותג
                  ו/או של צד ג' שהוא שאישר את השימוש בהם לה או למי מטעמה.
                </p>
              </div>
            </section>

            {/* הפסקת הפעילות באתר */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                הפסקת הפעילות באתר
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>35.</strong> החברה שומרת לעצמה את הזכות להפסיק בכל עת,
                  ועל פי שיקול דעתה הבלעדי את הפעילות באתר ו/או לביטול רכישת
                  מנוי של לקוח.
                </p>

                <p>
                  <strong>36.</strong> במידה ובוטלה הזמנה על פי החלטת החברה,
                  יזוכה הלקוח במלוא הסכום אותו שילם.
                </p>

                <p>
                  <strong>37.</strong> ככל והאתר יפסיק את פעילותו ו/או תבטל
                  רכישת מנוי מסוים של לקוח, בגין טעות בתיאור המוצר, מחיר שגוי
                  ו/או כל סיבה שהיא, לא תהיה ללקוח כל טענה ו/או תביעה כלפי החברה
                  ו/או מי ממפעילי האתר. לחילופין ולפנים משורת הדין, רשאית החברה
                  לאשר את תנאי העסקה החדשה בהתאם לשיקול דעתה הבלעדי.
                </p>
              </div>
            </section>

            {/* שונות */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                שונות
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>38.</strong> תנאי השימוש באתר מתקיימים באתר ובשירותים
                  הכלולים בו באמצעות כל מחשב או מכשיר תקשורת אחר (כגון: טלפון
                  סלולרי, טאבלט, מחשב יד וכו'). הם חלים על השימוש באתר גם ברשת
                  האינטרנט וגם בכל רשת או אמצעי תקשורת אחר.
                </p>

                <p>
                  <strong>39.</strong> החברה שומרת לעצמה את הזכות לשנות את תקנון
                  האתר מפעם לפעם, להוסיף או לגרוע ממנו, ביחס לאתר כולו, לחלקו על
                  פי שיקול דעתה הבלעדי וזאת ללא צורך במתן התראה ו/או הודעה
                  מוקדמת. האחריות הבלעדית והמלאה להכרת התנאים להלן ולשימוש באתר
                  על פי תנאים אלו חלה בכל עת על המשתמש בלבד.
                </p>

                <p>
                  <strong>40.</strong> החברה עושה ככל שיכולה לדאוג שהמידע שמופיע
                  באתר יהיה המידע המדויק ביותר אבל יובהר כי עלולים להופיע בו,
                  בתום לב אי-דיוקים מסוימים או טעויות והחברה לא תישא באחריות כל
                  שהיא הנובעת מהן או קשורה אליהן, ט.ל.ח.
                </p>

                <p>
                  <strong>41.</strong> כל התנהגות ו/או התנהלות של החברה לטובת
                  משתמש האתר מחוץ לגבולות שימוש האתר ובניגוד לאמור בתקנון, הינה
                  לפנים משורת הדין בלבד ולא תשמש או תיחשב כויתור מצידה על האמור
                  בתקנון זה.
                </p>

                <p>
                  <strong>42.</strong> במידה ויחולו תקלות מחשוב אשר ימנעו באופן
                  זמני שימוש באתר, תפעל החברה לטיפול בהם בהקדם ולא יהיו למשתמשי
                  האתר טענות או תביעות כנגדה בקשר לאירועים בתקופת התקלות והטיפול
                  בהן.
                </p>

                <p>
                  <strong>43.</strong> בכל מקרה של אי התאמה ו/או סתירה בין הקבוע
                  בתקנון זה, לבין הנאמר בכל פרסום ו/או מידע אחר שפורסם ו/או ניתן
                  על ידי החברה ו/או מי מטעמה, בקשר עם תנאי השימוש ורכישת מוצרים
                  באמצעות האתר, לרבות ברשתות החברתיות, תגברנה הוראות תקנון זה,
                  לכל דבר ועניין.
                </p>

                <p>
                  <strong>44.</strong> המחירים באתר כוללים מע״מ על פי הדין.
                </p>

                <p className="bg-blue-50 border-r-4 border-blue-400 p-4 rounded">
                  <strong>45.</strong> הדין החל על הזמנתך ועל תקנון זה, הוא הדין
                  בישראל בלבד. סמכות השיפוט הבלעדית לכל עניין ומחלוקת, נתונה
                  לבית משפט בחיפה בלבד.
                </p>
              </div>
            </section>

            {/* מדיניות פרטיות ושמירת סודיות */}
            <section className="mb-12 bg-gradient-to-r from-purple-50 to-blue-50 p-8 rounded-lg border-2 border-purple-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-2 border-purple-500 pb-2">
                🔒 מדיניות פרטיות ושמירת סודיות
              </h2>

              <div className="text-lg text-gray-800 mb-6">
                החברה מכבדת את פרטיות המשתמשים באתר ורואה חשיבות רבה בשמירה
                עליה. מטרת מדיניות הפרטיות המובאת להלן הינה לפרט את יחס החברה
                המפעילה את האתר לכל נושא הפרטיות של המשתמשים באתר, לרבות המידע
                שמסרו המשתמשים באתר, המידע שנאסף על נוהגיהם באתר והשימוש שעושה
                החברה במידע זה.
              </div>

              {/* המידע שנאסף */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  המידע שנאסף
                </h3>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>46.</strong> הלקוח באתר יתבקש למסור את מידע בסיסי,
                    שם מלא לאדם פרטי וכן לחברה, מספר עוסק, מסירת מספר זהות אינו
                    נתון חובה, כתובת דואר אלקטרוני, מספר טלפון סלולארי. החברה
                    נוקטת באמצעי הזהירות המקובלים על מנת לשמור ולהגן ככל הניתן
                    על סודיות המידע האישי של לקוחותיה והיא תעשה בו שימוש לצרכים
                    פנימיים בלבד, לרבות יצירת קשר עם המשתמשים, קבלת הצעות מחיר
                    למשתמשים, פרסומות ומידע שיווקי של החברה במידע והמשתמש אישר
                    זאת באתר.
                  </p>

                  <p>
                    <strong>47.</strong> הינך מצהיר כי כל הפרטים האישיים,
                    הנמסרים על ידך באתר, הינם נכונים ומלאים וכי הם נמסרים מרצונך
                    החופשי ובהסכמתך המלאה, מבלי שחלה עליך חובה כלשהי למסרם. כאשר
                    אתה מוסר באתר פרטים אישיים של צד שלישי, אתה מצהיר כי המידע
                    נמסר בהסכמתו המפורשת של הצד השלישי.
                  </p>

                  <p>
                    <strong>48.</strong> כאשר אתה משתמש באתר, יתכן שנאסף עליך
                    מידע כגון:
                  </p>

                  <ul className="list-disc list-inside mr-6 space-y-2">
                    <li>
                      מידע על המחשב שלכם, כולל כתובת ה-IP שלכם, מיקומכם
                      הגאוגרפי, סוג הדפדפן והגרסה שלו, ומערכת ההפעלה;
                    </li>
                    <li>
                      מידע על הביקורים והשימוש שלכם באתר, כולל מקור ההפניה, אורך
                      הביקור, צפיות בעמודים, ונתיבי המעבר שלכם באתר;
                    </li>
                    <li>
                      מידע, כמו למשל כתובת הדואר האלקטרוני שלכם, שאתם מזינים
                      בזמן ההרשמה לאתר שלנו;
                    </li>
                    <li>המידע שאתם מזינים בזמן השימוש בשירותים באתר שלנו;</li>
                    <li>
                      מידע שנוצר בזמן השימוש באתר שלנו, כולל מתי, כמה, ובאילו
                      נסיבות אתם משתמשים בו;
                    </li>
                    <li>
                      מידע שקשור לכל דבר שאתם רוכשים, שירותים בהם אתם משתמשים
                      באתר כולל את השם שלכם, הכתובת, מספר הטלפון, כתובת הדואר
                      האלקטרוני.
                    </li>
                    <li>
                      בנוסף, אם התחברת באתר באמצעות מנגנון התחברות צד שלישי –
                      כמו למשל – התחברות באמצעות חשבון ג'ימייל, טיקטוק, אינסטגרם
                      פייסבוק ועוד, יתכן ויאספו עליך פרטים אישיים ומידע נוסף
                      שיועברו מהצד השלישי לאתר.
                    </li>
                  </ul>

                  <p className="bg-yellow-50 border-r-4 border-yellow-400 p-4 rounded">
                    <strong>49.</strong> אם הינך מתחת לגיל 18, הינך מאשר כי
                    קיבלת את הסכמת אחד ההורים או האפוטרופוס החוקי שלך בטרם
                    השימוש באתר ומסירת הפרטים האישיים ומסירת פרטי התשלום באתר.
                  </p>

                  <p>
                    <strong>50.</strong> כאשר אתה משתמש באתר, יתכן שנאסף עליך
                    מידע כגון העמודים בהם צפית, מידע שקראת, מוצרים שרכשת, פרטי
                    ההתחברות שלך – סוג המכשיר סוג הדפדפן כתובת ה IP, ועוד. אם
                    התחברת באתר באמצעות מנגנון התחברות צד שלישי – כמו למשל –
                    התחברות באמצעות חשבון ג'ימייל או פייסבוק, יתכן ויאספו עליך
                    פרטים אישיים ומידע נוסף שיועברו מהצד השלישי לאתר. המידע
                    שמסרת באתר והמידע שנאסף עליך בעת השימוש באתר כפופים למדיניות
                    פרטיות זו ולהוראות כל דין לרבות החוק להגנת הפרטיות התשמ"א –
                    1981, תקנות הגנת הפרטיות (אבטחת מידע), התשע"ז – 2017 וככל
                    ש־GDPR חל – תפעל החברה בהתאם להוראותיו.
                  </p>

                  <p className="bg-blue-50 border-r-4 border-blue-400 p-4 rounded">
                    <strong>51.</strong> פרטיו האישים של המשתמש (דואר אלקטרוני
                    ו/או מספר נייד בלבד), יישמרו על פי בקשת המשתמש באתר לטובת
                    דיוור ישיר. להסרה יש לפנות בכתב לשירות הלקוחות במייל:{" "}
                    <a
                      href="mailto:info@cartax.org"
                      className="text-blue-600 hover:text-blue-800 underline font-semibold"
                    >
                      info@cartax.org
                    </a>{" "}
                    ו/או בטלפון:{" "}
                    <a
                      href="tel:054-4512136"
                      className="text-blue-600 hover:text-blue-800 underline font-semibold"
                    >
                      054-4512136
                    </a>
                    . ביצוע ההסרה עתיד להימשך כ-14 ימי עבודה, מיום קבלת הפנייה.
                  </p>
                </div>
              </div>

              {/* שימוש במידע */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  שימוש במידע
                </h3>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>52.</strong> החברה תעשה שימוש בפרטי המשתמשים הרשומים
                    באתר לצרכי תפעול האתר ועל מנת לאפשר את ביצוע הרכישה באתר,
                    וכן, לצורך שמירת קשר עם המשתמש, על מנת לעדכן במבצעים,
                    אירועים, הטבות, הודעות, סטטוס הזמנה, חדשות וכו' בהתאם
                    להעדפות הלקוח בעת שהותו באתר ובהתאם להעדפות החברה בהודעות
                    דוא"ל ומסרונים.
                  </p>

                  <p>
                    <strong>53.</strong> המשתמש מצהיר כי הוא מודע לכך שהחברה
                    עשויה לאסוף ולעבד נתונים סטטיסטיים/מצרפיים שאינם מזהים אישית
                    על דפוסי שימוש באתר. נתונים אלו ישמשו את החברה לצורכי ניתוח,
                    בקרה ושיפור השירות, ויכולים להימסר לצדדים שלישיים למטרות אלו
                    בלבד. מובהר כי לא יימסרו פרטים המזהים משתמשים בודדים אלא אם
                    קיימת לכך עילה חוקית מתאימה (לרבות צו שיפוטי) או הסכמה
                    מפורשת של המשתמש.
                  </p>

                  <p>
                    <strong>54.</strong> האתר עושה שימוש ב-Cookies וכלי אנליטיקה
                    לשיפור חוויית השימוש, אבטחה וסטטיסטיקה. ניתן לנהל/לחסום
                    Cookies בהגדרות הדפדפן; חסימה עשויה לפגוע בחלק מהתפקוד.
                  </p>

                  <p>
                    <strong>55.</strong> החברה מייחסת חשיבות עליונה לאבטחת המידע
                    של לקוחותיה, כל מידע המתקבל באתר, בין אם נמסר על-ידי הלקוח
                    ובין אם נאסף על-ידי Cookie בעת שימוש באתר, הוא לשימוש בלעדי
                    של האתר לצורך תיעוד רכישות הלקוח וכן לטובת מבצעים עתידיים
                    (כפוף למתן הסכמת הלקוח להיכלל ברשימת התפוצה של האתר).
                  </p>

                  <p>
                    <strong>56.</strong> בכל מקרה, החברה תהא רשאית להעביר פרטיו
                    האישיים של משתמש לצד שלישי במקרים בהם המשתמש ביצע מעשה או
                    מחדל הפוגעים ו/או העלולים לפגוע בחברה ו/או בצדדים שלישיים
                    כלשהם, המשתמש עשה שימוש בשירותי החברה לביצוע מעשה בלתי חוקי,
                    אם התקבל בידי החברה צו שיפוטי המורה לה למסור את פרטי המשתמש
                    לצד שלישי וכן בכל מחלוקת או הליך משפטי.
                  </p>
                </div>
              </div>

              {/* הרשאה לדיוור */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  הרשאה לדיוור, פרסומים ופרסומות
                </h3>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>57.</strong> גולש שהשאיר פרטים באתר ומצורף לרשימת
                    הדיוור של האתר, מאשר שימוש בפרטיו לצורך קבלת מידע שיווקי,
                    עדכונים ופרסומות שיבצע האתר מעת לעת.
                  </p>

                  <p>
                    <strong>58.</strong> על גולש שהשאיר פרטים כאמור תחולנה
                    הוראות הדיוור המפורטות בתקנון להלן.
                  </p>

                  <p>
                    <strong>59.</strong> אין להשאיר פרטים של אדם אחר באתר שלא
                    בהסכמתו ו/או ללא נוכחותו מול המסך בשעת השארת הפרטים ולאחר
                    שהוסברו לו כל תנאי התקנון.
                  </p>

                  <p>
                    <strong>60.</strong> בעת השארת הפרטים יתבקש הגולש למסור
                    פרטים אישיים כגון: שם פרטי, שם משפחה, טלפון וכן כתובת דואר
                    אלקטרוני פעילה (לשיקול דעתה הבלעדי של החברה). מסירת פרטים
                    חלקיים או שגויים עלולים למנוע את האפשרות להשתמש בשירות ולסכל
                    יצירת קשר במקרה הצורך. במקרה של שינוי פרטים יש לעדכנם באתר.
                  </p>

                  <p>
                    <strong>61.</strong> מובהר כי אין חובה על-פי חוק למסור פרטים
                    באתר, אולם מי שלא יירשם כמנוי לא יוכל לקבל עדכנים, חדשות,
                    מבצעים ולפנות לשירותי הייעוץ שינתנו מעת לעת.
                  </p>

                  <p>
                    <strong>62.</strong> החברה לא תעשה בפרטים שנמסרו שימוש, אלא
                    בהתאם למדיניות הפרטיות של האתר המהווה חלק בלתי נפרד מהתקנון.
                  </p>

                  <p>
                    <strong>63.</strong> השארת פרטים באתר ואישור לקבלת תוכן
                    שיווקי כוללת, בין היתר, קבלת תוכן שיווקי, מידע ביחס למבצעים,
                    עדכונים והנחות המוצעים למשתמשים רשומים.
                  </p>

                  <p>
                    <strong>64.</strong> אישור דיוור (קבלת תוכן שיווקי) כאמור,
                    מהווה את הסכמתו של הגולש למשלוח דברי פרסומת על-פי חוק
                    התקשורת (בזק ושידורים) (תיקון מס' 40) התשס"ח – 2008 ("חוק
                    התקשורת").
                  </p>

                  <p>
                    <strong>65.</strong> שליחת דבר פרסומת תיעשה רק בהסכמה מפורשת
                    של המשתמש; בכל הודעה יצורף קישור הסרה קל וברור, וההסרה תבוצע
                    ללא דיחוי לא סביר.
                  </p>

                  <p>
                    <strong>66.</strong> מובהר כי באפשרות משאיר הפרטים להסיר
                    עצמו בכל עת מהדיוור באמצעות לחיצה על "להסרה מרשימת התפוצה"
                    או כל מלל דומה שמופיע בתחתית כל דיוור שנשלח או באמצעות פנייה
                    לאתר בדוא"ל. כל עוד לא הסיר עצמו הנרשם מרשימת הדיוור כאמור,
                    רשאית החברה, בכפוף לחוק התקשורת, להעביר לנרשם דיוור ישיר
                    כאמור.
                  </p>

                  <p>
                    <strong>67.</strong> החברה רשאית לבטל את רישומו של גולש
                    לרשימת הדיוור לפי שיקול דעתו המוחלט. מבלי לגרוע מהאמור לעיל,
                    רשאית החברה למנוע גלישת גולש ו/או לבטל רישום לרשימת הדיוור,
                    או לחסום גישה אליו בכל אחד מהמקרים הבאים:
                  </p>

                  <ul className="list-disc list-inside mr-6 space-y-2">
                    <li>
                      <strong>א.</strong> אם בעת השארת הפרטים ו/או הרכישה באתר
                      נמסרו במתכוון פרטים שגויים;
                    </li>
                    <li>
                      <strong>ב.</strong> במקרה שנעשה שימוש באתר לביצוע או כדי
                      לנסות לבצע מעשה בלתי חוקי על-פי דיני מדינת ישראל, או מעשה
                      הנחזה על פניו כבלתי חוקי כאמור, או כדי לאפשר, להקל, לסייע
                      או לעודד ביצועו של מעשה כזה;
                    </li>
                    <li>
                      <strong>ג.</strong> אם הופרו תנאי התקנון;
                    </li>
                    <li>
                      <strong>ד.</strong> אם נעשתה על ידי גולש כל פעולה שתמנע
                      מאחרים להמשיך ולהשתמש באתר בכל דרך שהיא.
                    </li>
                    <li>
                      <strong>ה.</strong> אם, ועל פי שיקול דעתה הבלעדי של החברה,
                      נראה שיש בפעולות המשתמש כדי להיטיב ו/או לאסוף מידע ו/או
                      להשתמש במערכת עם ו/או באמצעות מתחרה עסקי של החברה.
                    </li>
                  </ul>

                  <p>
                    <strong>68.</strong> אין לראות במידע בדיוור משום הבטחה
                    לתוצאה כלשהי ו/או אחריות למוצר ו/או לשירות המוצע בו.
                  </p>

                  <p>
                    <strong>69.</strong> החברה לא תהיה אחראית לכל נזק (ישיר או
                    עקיף), הפסד, עגמת נפש והוצאות שייגרמו למשאיר פרטים ו/או
                    לצדדים שלישיים כלשהם בעקבות שימוש או הסתמכות על כל תוכן,
                    מידע, נתון, מצג, פרסומת, מוצר, שירות וכד' המופיעים בדיוור.
                    כל הסתמכות כאמור נעשית על-פי שיקול דעתו ואחריותו הבלעדית של
                    משאיר הפרטים.
                  </p>

                  <p>
                    <strong>70.</strong> הדיוור בכללותו, לרבות כל המידע המופיע
                    בו, מוצע כמות שהוא, ויהיה מדויק ונכון ככל הניתן, ואולם, יתכן
                    והמידע אינו שלם או לחלופין, יתכן ונפלו טעויות טכניות או
                    אחרות במידע.
                  </p>

                  <p>
                    <strong>71.</strong> הגולש מאשר כי לא תהיה לו כל טענה בקשר
                    למודעות פרסום ו/או פרסומות המוצגות באתר, לרבות בקשר למיקומן
                    באתר. מובהר כי בכל הנוגע למודעות פרסום המוצגות בחסות צד
                    שלישי, לאתר אין כל התערבות בבחירת הפרסומות המוצגות.
                  </p>
                </div>
              </div>

              {/* נהלי אבטחה */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  נהלי אבטחה
                </h3>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>72.</strong> החברה משתמשת בתקני האבטחה הגבוהים ביותר
                    על מנת לשמור ככל האפשר על סודיות המידע ופרטיות לקוחותיה.
                  </p>

                  <p>
                    <strong>73.</strong> האתר מאובטח באמצעות פרוטוקול SSL, כלומר
                    כל התקשורת שבין הדפדפן (אצל הלקוח) לאתר (כלומר שרת האינטרנט
                    המאחסן את העמודים) הינה מוצפנת כך שלא ניתן לפענח את המידע
                    המועבר בין הדפדפן לאתר.
                  </p>

                  <p>
                    <strong>74.</strong> במקרים שאינם בשליטה ו/או נובעים מכוח
                    עליון, החברה לא תישא באחריות לכל נזק מכל סוג שהוא, עקיף או
                    ישיר שיגרם למשתמש ו/או למי מטעמו, אם מידע יאבד או יגיע לגורם
                    עוין ו/או יעשה בו שימוש שלא בהרשאה.
                  </p>

                  <p>
                    <strong>75.</strong> החברה עושה מאמץ לספק למשתמש שירות תקין
                    ובאיכות גבוהה. יחד עם זאת, החברה אינה מתחייבת שהשירות באתר
                    לא יופרע, יינתן כסדרו או בלא הפסקות, יתקיים בבטחה וללא
                    טעויות, ויהיה חסין מפני גישה בלתי-מורשית למחשבי החברה,
                    נזקים, קלקולים, תקלות או כשלים - לרבות תקלות בחומרה, בתוכנה
                    או בקווי התקשורת לאתר - אצל החברה או אצל מי מספקיה.
                  </p>

                  <p>
                    <strong>76.</strong> המידע עליך מאוחסן במאגרי מידע המנוהלים
                    על ידי החברה ו/או על ידי צד שלישי מטעמה בכפוף לתקנות הגנת
                    הפרטיות (אבטחת מידע), התשע"ז-2017. מאגרי המידע עשויים להימצא
                    מחוץ לגבולות מדינת ישראל. בעצם השימוש באתר, אתה מסכים להעברת
                    המידע עליך ולשמירתו מחוץ לגבולות ישראל. העברת מידע מחוץ
                    לישראל תיעשה בהתאם לדין החל ובכפוף להבטחת רמת הגנה נאותה;
                    וככל שה־GDPR חל – באמצעות מנגנוני העברה מתאימים (לרבות
                    סטנדרט קונטרקטואל קלאוזס, ככל שיידרש).
                  </p>
                </div>
              </div>

              {/* הזכות לעיון במידע */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  הזכות לעיון במידע
                </h3>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>77.</strong> לפי חוק הגנת הפרטיות, התשמ"א-1981, הינך
                    רשאי לעיין במידע עליך המוחזק במאגרי המידע של האתר. באם תמצא
                    כי המידע עליך אינו נכון, שלם, ברור או מעודכן, הינך רשאי
                    לפנות לחברה בבקשה לתקן את המידע עליך או למוחקו. אם המידע
                    שבמאגרי המידע של האתר משמש לצורך פניה אישית אליך, הינך זכאי
                    לדרוש שמידע זה יימחק.
                  </p>

                  <p>
                    <strong>78.</strong> כל פנייה שלך לחברה בנוגע לעיון במידע
                    עליך ו/או שינויו, תעשה בכתב לפי הפרטים המופיעים בעמוד יצירת
                    הקשר באתר. מחובתך לוודא כי פנייתך הגיעה לחברה ומטופלת על
                    ידה. החברה מסירה מעצמה אחריות לפניות שלא הגיעו אליה ו/או
                    שהגיעו אליה שלא בכתב ו/או שאינן קריאות וברורות.
                  </p>

                  <p className="bg-red-100 border-r-4 border-red-500 p-4 rounded">
                    <strong>79.</strong> אם לדעתך האתר פוגע בפרטיות המשתמשים בו
                    ככלל, או בפרטיותך כפרט, אנא פנה לחברה בכתובת{" "}
                    <a
                      href="mailto:info@cartax.org"
                      className="text-red-700 hover:text-red-900 underline font-semibold"
                    >
                      info@cartax.org
                    </a>
                  </p>
                </div>
              </div>

              {/* שינוי במדיניות הפרטיות */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  שינוי במדיניות הפרטיות
                </h3>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>80.</strong> החברה שומרת לעצמה את הזכות לעדכן מעת
                    לעת וללא כל הודעה מוקדמת את מדיניות הפרטיות. השינויים
                    במדיניות הפרטיות ייכנסו לתוקף בתוך 48 שעות מרגע העדכון.
                    החברה רשאית על פי שיקול דעתה לעדכן את המשתמשים בשינוי
                    במדיניות הפרטיות. אם יבוצעו במדיניות שינויים מהותיים, תפורסם
                    על-כך הודעה בעמוד הבית של האתר 30 ימים לפני כניסת השינויים
                    לתוקף. המשך שימושך באתר מהווה הסכמה לנוסח המעודכן של מדיניות
                    הפרטיות. אם אינך מסכים עם הנוסח המעודכן, עליך להפסיק את
                    השימוש באתר.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section className="mt-12 bg-blue-50 border-2 border-blue-300 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                <span>📞</span>
                <span>יצירת קשר</span>
              </h2>

              <div className="space-y-3 text-gray-800">
                <p>
                  <strong>דוא"ל:</strong>{" "}
                  <a
                    href="mailto:info@cartax.org"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    info@cartax.org
                  </a>
                </p>
                <p>
                  <strong>טלפון:</strong>{" "}
                  <a
                    href="tel:054-4512136"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    054-4512136
                  </a>
                </p>
                <p>
                  <strong>אתר:</strong>{" "}
                  <a
                    href="https://www.cartax.org"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    www.cartax.org
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* Print Button */}
          <div className="mt-8 flex justify-center print:hidden">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              aria-label="הדפס עמוד"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              <span>🖨️ הדפס עמוד</span>
            </button>
          </div>
        </div>
      </main>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default TermsAndPrivacyPage;
